import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

export const useTradeHitDetector = (trades) => {
  const currentPrice = useSelector((state) => state.trade.currentPrice);
  const lastCheckedPriceRef = useRef(null);
  
  const checkAllTradeHits = useCallback((newPrice) => {
    // Skip if no price or no trades
    if (!trades?.length || !newPrice || newPrice === lastCheckedPriceRef.current) {
      return { hits: [], summary: null };
    }

    console.log(`Checking all trade hits at price: ${newPrice}`);
    
    const hits = [];
    let tpHits = 0;
    let slHits = 0;
    let limitHits = 0;

    trades.forEach(trade => {
      const { tp, sl, long, user_trade_index, is_open, trade_type, open_price } = trade;
      
      // Skip closed trades
      if (!is_open) return;

      // CHECK TP/SL HITS (for active trades only)
      if (trade_type !== 'limit') {
        // TP Hit Check
        if (tp) {
          const tpPrice = parseFloat(tp);
          const shouldHitTP = (long && newPrice >= tpPrice) || (!long && newPrice <= tpPrice);
          
          if (shouldHitTP) {
            hits.push({
              tradeIndex: user_trade_index,
              type: 'TP',
              direction: long ? 'LONG' : 'SHORT',
              triggerPrice: tpPrice,
              currentPrice: newPrice,
              category: 'tp_sl'
            });
            tpHits++;
            console.log(`TP HIT: Trade ${user_trade_index} (${long ? 'LONG' : 'SHORT'}) - Price: ${newPrice}, TP: ${tpPrice}`);
          }
        }

        // SL Hit Check
        if (sl) {
          const slPrice = parseFloat(sl);
          const shouldHitSL = (long && newPrice <= slPrice) || (!long && newPrice >= slPrice);
          
          if (shouldHitSL) {
            hits.push({
              tradeIndex: user_trade_index,
              type: 'SL',
              direction: long ? 'LONG' : 'SHORT',
              triggerPrice: slPrice,
              currentPrice: newPrice,
              category: 'tp_sl'
            });
            slHits++;
            console.log(`🛑 SL HIT: Trade ${user_trade_index} (${long ? 'LONG' : 'SHORT'}) - Price: ${newPrice}, SL: ${slPrice}`);
          }
        }
      }

      // 📈 CHECK LIMIT ORDER HITS
      if (trade_type === 'limit' && open_price) {
        const limitPrice = parseFloat(open_price);
        const shouldExecuteLimit = (long && newPrice <= limitPrice) || (!long && newPrice >= limitPrice);
        
        if (shouldExecuteLimit) {
          hits.push({
            tradeIndex: user_trade_index,
            type: 'LIMIT_ORDER',
            direction: long ? 'LONG' : 'SHORT',
            triggerPrice: limitPrice,
            currentPrice: newPrice,
            category: 'limit_order'
          });
          limitHits++;
          console.log(`📈 LIMIT ORDER HIT: Trade ${user_trade_index} (${long ? 'LONG' : 'SHORT'}) - Price: ${newPrice}, Limit: ${limitPrice}`);
        }
      }
    });

    // Update last checked price
    lastCheckedPriceRef.current = newPrice;

    const summary = {
      totalHits: hits.length,
      tpHits,
      slHits,
      limitHits,
      price: newPrice
    };

    if (hits.length > 0) {
      console.log(`⚡ TOTAL HITS DETECTED: ${hits.length} (TP: ${tpHits}, SL: ${slHits}, Limit: ${limitHits})`);
    }

    return { hits, summary };
  }, [trades]);

  return { checkAllTradeHits };
};